import axios from 'axios';
import { HttpError } from 'react-admin';

import type { AuthUserType } from '../auth/types';
import { getAPIFormErrors } from './helpers';
import { clearAuthInLocalStorage, getAuthFromLocalStorage, updateAuthInLocalStorage } from '../auth/authProvider';
import { CacheRequestConfig, setupCache } from 'axios-cache-interceptor';

const axiosApiInstance = axios.create({
    baseURL: `${process.env.REACT_APP_JOURNAL_SERVICE || ''}${process.env.REACT_APP_API_BASE_PATH || ''}`
});
setupCache(axiosApiInstance);

export function initAxios() {
    axiosApiInstance.interceptors.request.use(
        async (config) => {
            const authUser: AuthUserType = getAuthFromLocalStorage();
            if (!authUser) {
                return config;
            }

            config.headers = {
                Authorization: `Bearer ${authUser.access_token}`,
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded',
                ...(config.headers || {})
            } as any;
            return config;
        },
        (error) => Promise.reject(error)
    );

    axiosApiInstance.interceptors.response.use(
        (response) => {
            return response;
        },
        async function (error) {
            const {
                config: originalRequest,
                response: { status, data }
            } = error;
            const authUser: AuthUserType = getAuthFromLocalStorage();
            if (authUser && status === 401 && !originalRequest._retry && originalRequest.url != '/auth/refresh') {
                originalRequest._retry = true;

                const newAccessToken = await refreshAccessToken(authUser.refresh_token);
                originalRequest.headers = {
                    ...(originalRequest.headers || {}),
                    Authorization: `Bearer ${newAccessToken}`
                };
                return axiosApiInstance(originalRequest);
            }

            if (authUser && status === 401) {
                if (data.message === 'Користувач має оновити медичні дані') {
                    window.location.href = '/restricted';
                    return Promise.resolve();
                } else {
                    clearAuthInLocalStorage();
                }
            }

            if (status === 400) {
                const errorMessage = error.response?.data?.message || 'Запит відхилено. Перевірте помилки.';
                return Promise.reject(
                    new HttpError(errorMessage, status, {
                        errors: getAPIFormErrors(data.errors)
                    })
                );
            }

            return Promise.reject(error);
        }
    );
}

function refreshAccessToken(refreshToken: string): Promise<string> {
    return axiosApiInstance
        .get('/auth/refresh', { headers: { Authorization: `Bearer ${refreshToken}` } })
        .then((response) => {
            updateAuthInLocalStorage(response.data);
            return response.data.access_token;
        });
}

export const postRequestCacheConfig: CacheRequestConfig<any, unknown> = {
    cache: { methods: ['post'], interpretHeader: false }
};

/**
 * Caches data with response status 200 for needed minutes
 * @param { number } [ttlMinutes = 2] - time to load, defaults to 2
 * @param { number[] } [cacheableStatusCodes = [200]] - response status codes that could be accepted to cache the data, defaults to [200]
 */
export const getRequestCacheConfig = (
    ttlMinutes = 2,
    cacheableStatusCodes = [200]
): CacheRequestConfig<any, unknown> => {
    return {
        cache: {
            methods: ['get'],
            interpretHeader: false,
            cachePredicate: {
                statusCheck: (status: number): boolean => cacheableStatusCodes.includes(status)
            },
            ttl: 1000 * 60 * ttlMinutes
        }
    };
};

export default axiosApiInstance;
